import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import type { Vehicle } from '../types/gtfs-rt';
import { getVehicles } from '../services/trafiklab';

interface UseVehicleTrackerProps {
    selectedOperator: string;
    realtimeApiKey: string;
    pollInterval: number;
    isPaused?: boolean;
}

interface UseVehicleTrackerResult {
    vehicles: Vehicle[];
    isLoading: boolean;
    lastUpdate: Date | null;
    error: Error | null;
    refetch: () => Promise<void>;
}

export function useVehicleTracker({
    selectedOperator,
    realtimeApiKey,
    pollInterval,
    isPaused = false
}: UseVehicleTrackerProps): UseVehicleTrackerResult {
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const [error, setError] = useState<Error | null>(null);

    // Use refs to access latest props in timeouts/intervals without triggering re-effects
    const propsRef = useRef({ selectedOperator, realtimeApiKey, pollInterval, isPaused });

    useEffect(() => {
        propsRef.current = { selectedOperator, realtimeApiKey, pollInterval, isPaused };
    }, [selectedOperator, realtimeApiKey, pollInterval, isPaused]);

    const fetchVehicles = useCallback(async (isAutoPoll = false) => {
        const { selectedOperator, realtimeApiKey } = propsRef.current;

        if (!realtimeApiKey) return;

        if (!isAutoPoll) setIsLoading(true);
        setError(null);

        try {
            const vehicleData = await getVehicles(selectedOperator, realtimeApiKey);
            setVehicles(vehicleData);
            setLastUpdate(new Date());

            if (!isAutoPoll) {
                console.log(`Fetched ${vehicleData.length} vehicles`);
            }
        } catch (err) {
            console.error('Failed to fetch vehicles:', err);
            const errorObj = err instanceof Error ? err : new Error('Unknown error');
            setError(errorObj);

            const errorMessage = errorObj.message;

            // Check for rate limit errors
            if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
                toast.error('Rate limit exceeded! Try increasing the update interval.', {
                    duration: 5000
                });
            } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
                toast.error('Invalid API key. Please check your settings.', {
                    duration: 5000
                });
            } else if (!isAutoPoll) {
                // Only show toast for manual fetches or critical errors to avoid spamming
                toast.error(`Failed to fetch vehicles: ${errorMessage}`, {
                    duration: 4000
                });
            }
        } finally {
            if (!isAutoPoll) setIsLoading(false);
        }
    }, []);

    // Polling logic using recursive setTimeout
    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;
        let mounted = true;

        const poll = async () => {
            const { isPaused, pollInterval, realtimeApiKey } = propsRef.current;

            if (!mounted) return;

            if (!isPaused && realtimeApiKey) {
                await fetchVehicles(true);
            }

            // Schedule next poll
            if (mounted) {
                timeoutId = setTimeout(poll, pollInterval);
            }
        };

        // Initial fetch
        if (realtimeApiKey && !isPaused) {
            fetchVehicles(false).then(() => {
                // Start polling after initial fetch completes
                timeoutId = setTimeout(poll, pollInterval);
            });
        }

        return () => {
            mounted = false;
            clearTimeout(timeoutId);
        };
    }, [selectedOperator, realtimeApiKey, isPaused, pollInterval, fetchVehicles]);

    return {
        vehicles,
        isLoading,
        lastUpdate,
        error,
        refetch: () => fetchVehicles(false)
    };
}
